/**
 * Two-way bridge between the in-memory highlight overlay (useHighlightStore)
 * and a Yjs Y.Map living in the document's Y.Doc, so highlights/codes appear
 * live for every collaborator.
 *
 * Highlights are keyed by their stable `id` (the same id across peers, since it
 * comes from highlights.json which the existing cloud sync round-trips), so
 * merging is idempotent — the same id just carries the latest value. Deletions
 * are soft (attrs.isDeleted === "true", set by highlightStore.removeHighlight):
 * a tombstone is never removed from the map and always wins over a live copy,
 * so a delete propagates and can't be resurrected by a peer with a stale copy.
 *
 * Loop prevention: our own store→map writes are tagged with LOCAL_ORIGIN; the
 * map observer ignores that origin, and while applying a remote map→store
 * change we set `applyingRemote` so the store subscription doesn't echo it back.
 * (The autosave subscription still fires on remote changes — intentional, so
 * each peer keeps its durable highlights.json current.)
 */
import * as Y from "yjs";
import type { Highlight } from "../types";
import { useHighlightStore } from "../store/highlightStore";

const LOCAL_ORIGIN = Symbol("qda-highlights-local");

export interface HighlightsBridge {
  destroy(): void;
}

export function createHighlightsBridge(ydoc: Y.Doc): HighlightsBridge {
  const ymap = ydoc.getMap<Highlight>("highlights");
  let applyingRemote = false;

  const isTomb = (h?: Highlight): boolean => h?.attrs.isDeleted === "true";

  const writeStoreToMap = (highlights: Highlight[]) => {
    ydoc.transact(() => {
      for (const h of highlights) {
        const existing = ymap.get(h.id);
        // Tombstone wins: never overwrite a deleted entry with a live one.
        if (isTomb(existing) && !isTomb(h)) continue;
        if (!existing || JSON.stringify(existing) !== JSON.stringify(h)) {
          ymap.set(h.id, h);
        }
      }
      // Deletions are tombstones (kept), never map removals — so nothing is
      // deleted here; an absence in the store can't drop a peer's tombstone.
    }, LOCAL_ORIGIN);
  };

  // Initial reconciliation: adopt any shared state peers already published,
  // merged by id with this device's loaded highlights, then publish the union.
  //
  // `remote` isn't necessarily a genuine peer's data — this ymap is restored
  // from OUR OWN QdaDocument.ystate snapshot, which is only re-captured on
  // TEXT edits (persistFromEditorState), never on highlight-only changes
  // (persistHighlights has its own independent debounce — see autosave.ts).
  // So `remote` can be an older version of a highlight than what's already in
  // highlights.json (e.g. a code attached/changed with no subsequent text
  // edit before reload). Blindly letting remote win — as this used to do —
  // clobbered that fresher local copy on every refresh. Compare `modified`
  // instead so the same id really does carry "the latest value" (tombstones
  // still always win, so a delete can't be resurrected by a stale copy).
  const remote = Array.from(ymap.values());
  if (remote.length) {
    const byId = new Map<string, Highlight>();
    for (const h of useHighlightStore.getState().highlights) byId.set(h.id, h);
    for (const h of remote) {
      const local = byId.get(h.id);
      if (isTomb(local) && !isTomb(h)) continue;
      if (isTomb(h) || !local || h.modified > local.modified) byId.set(h.id, h);
    }
    applyingRemote = true;
    useHighlightStore.getState().setHighlights(Array.from(byId.values()));
    applyingRemote = false;
  }
  writeStoreToMap(useHighlightStore.getState().highlights);

  // store → map
  const unsub = useHighlightStore.subscribe((state, prev) => {
    if (applyingRemote) return;
    if (state.highlights === prev.highlights) return;
    writeStoreToMap(state.highlights);
  });

  // map → store
  const observer = (_event: Y.YMapEvent<Highlight>, transaction: Y.Transaction) => {
    if (transaction.origin === LOCAL_ORIGIN) return;
    applyingRemote = true;
    try {
      useHighlightStore.getState().setHighlights(Array.from(ymap.values()));
    } finally {
      applyingRemote = false;
    }
  };
  ymap.observe(observer);

  return {
    destroy() {
      unsub();
      ymap.unobserve(observer);
    },
  };
}
